import {CopyOutlined, DeleteFilled, PlusOutlined, ReloadOutlined} from "@ant-design/icons";
import {Breadcrumb, Button, Checkbox, message, Modal, Space, Table} from "antd";
import _ from "lodash";
import React, {useContext, useEffect, useMemo, useState} from "react";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {AppContextContext, VulReportDefinitionServiceContext} from "../../Contexts";
import {useTableHandler} from "../../sal-ui/TableHandler";
import PagedResult from "../../service/PagedResult";
import {DocumentTitle} from "../DocumentTitle";
import * as styles from "./VulReportDefinitionList.module.css";
import * as globalStyles from "../App.module.css";
import {ColumnType} from "antd/es/table";
import VulReportDefinition from "../../domain/VulReportDefinition";
import {capitalize} from "../../utils/FormatUtils";
import {Permission} from "../../domain/auth/Permission";
import {renderOrganizationLink} from "../common/OrganizationLink";
import VulReportDefinitionFilter, {createDefaultVulReportDefinitionFilterValues} from "./VulReportDefinitionFilter";
import {FormPersister} from "../../sal-ui/FormPersister";
import {formatUpdatedItemsBehaviour, UpdatedItemsBehaviour} from "../../domain/UpdatedItemsBehaviour";
import {Feature} from "../../domain/Feature";
import {ascendingFirstSortDirections} from "../../sal-ui/TableSortDirections";

const PERSISTENT_IDENT = "VulReportDefinitionList";
const FILTER_DEBOUNCE_MS = 500;

function VulReportDefinitionList() {
    const appContext = useContext(AppContextContext);
    const vulReportDefinitionService = useContext(VulReportDefinitionServiceContext);
    const location = useLocation();
    const navigate = useNavigate();
    const tableHandler = useTableHandler("name", {reloadFunction: reload});
    const filterFormPersister = useMemo(() => new FormPersister(PERSISTENT_IDENT), []);
    const [definitions, setDefinitions] = useState<PagedResult<VulReportDefinition>>();
    const [filterValues, setFilterValues] = useState<any>();
    const canUseAiSummary = appContext.user.hasFeature(Feature.AI);

    const debouncedOnFinishFilter = useMemo(() => _.debounce(onFinishFilter, FILTER_DEBOUNCE_MS), []);

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const loadedValues = searchParams.has("filter")
            ? filterFormPersister.loadValues(FormPersister.decodeUrlState(searchParams.get("filter")!))
            : filterFormPersister.loadValues();

        const values = _.isEmpty(loadedValues)
            ? createDefaultVulReportDefinitionFilterValues()
            : {...createDefaultVulReportDefinitionFilterValues(), ...loadedValues};

        setFilterValues(values);

        if (searchParams.has("filter")) {
            navigate(location.pathname, {replace: true});
        }
    }, [])

    useEffect(() => {
        if (filterValues !== undefined) {
            debouncedOnFinishFilter(filterValues);
        }
    }, [filterValues]);

    const title = "Vulnerability report definitions";

    const columns: ColumnType<VulReportDefinition>[] = [
        {
            dataIndex: "name",
            title: "Name",
            sorter: true,
            sortDirections: ascendingFirstSortDirections,
            defaultSortOrder: "ascend",
            className: styles["column-name"],
            render: (value, definition) => <Link to={`/definitions/vul-reports/${definition.id}`}>{value}</Link>
        },
        {
            dataIndex: "vulViews",
            title: "Vulnerability views",
            align: "left",
            render: renderVulViews
        },
        {
            dataIndex: "days",
            title: "Days",
            render: days => days?.length === 7
                ? <i>every day</i>
                : days?.map(day => <div key={day}>{capitalize(day.toLowerCase())}</div>)
        },
        {
            dataIndex: "times",
            title: "Hours",
            render: (value, definition) => `${value?.join(", ")} (${definition.timeZone})`
        },
        {
            dataIndex: "reportUpdatedItemsBehaviour",
            title: "Report updated items",
            render: renderReportUpdatedItems
        },
        {
            dataIndex: "enabled",
            title: "Enabled",
            align: "center",
            render: value => <Checkbox checked={value} disabled={true}/>
        },
        ...(canUseAiSummary ? [{
            dataIndex: "autoGenerateAiSummary",
            title: "AI summary",
            align: "center" as const,
            render: (value: boolean) => <Checkbox checked={value} disabled={true}/>
        }] : []),
        {
            dataIndex: "email",
            title: "E-mail",
        }
    ];

    if (appContext.user.hasPermission(Permission.REPORT__MANAGE)) {
        columns.push(
            {
                title: "Actions",
                width: 100,
                className: globalStyles["table-actions"],
                render: renderAction
            }
        );
    }

    if (appContext.user.isSystemAdmin()) {
        columns.splice(2, 0, {
            dataIndex: ["organization", "name"],
            title: "Organization",
            sorter: true,
            sortDirections: ascendingFirstSortDirections,
            align: "left",
            render: renderOrganization
        });
    }

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}>
                    <Breadcrumb.Item>{appContext.config?.appName}</Breadcrumb.Item>
                    <Breadcrumb.Item>{title}</Breadcrumb.Item>
                </Breadcrumb>

                <h1>{title}</h1>

                <VulReportDefinitionFilter
                    className={styles.filter}
                    showOrganization={appContext.user.isSystemAdmin()}
                    values={filterValues}
                    persistentStateIdent={PERSISTENT_IDENT}
                    onChange={setFilterValues}
                />

                <div className={globalStyles["common__top-button-bar"]}>
                    <Button className={"btn-seamless"} icon={<ReloadOutlined/>} onClick={tableHandler.reload}/>

                    {appContext.user.hasPermission(Permission.REPORT__MANAGE) &&
                        <Button type={"primary"}
                                icon={<PlusOutlined/>}
                                onClick={() => navigate("/definitions/vul-reports/add")}>Add</Button>
                    }
                </div>

                <Table className={styles.table}
                       showSorterTooltip={false}
                       dataSource={definitions?.data}
                       size="middle"
                       loading={tableHandler.loading}
                       onChange={tableHandler.onTableChange}
                       pagination={tableHandler.pagination}
                       onRow={onRow}
                       rowKey="id"
                       columns={columns}
                />
            </>
        </DocumentTitle>
    );

    function renderOrganization(value: string | undefined, definition: VulReportDefinition) {
        return renderOrganizationLink(definition.organization, value);
    }

    function renderVulViews(_value: unknown, definition: VulReportDefinition) {
        return definition.vulViews?.map(view => (
            <div key={view.id}>
                <Link to={`/vul-source-items/views/${view.id}`}>{view.name}</Link>
            </div>
        ));
    }

    function renderReportUpdatedItems(_value: unknown, definition: VulReportDefinition) {
        if (definition.reportUpdatedItemsBehaviour !== UpdatedItemsBehaviour.PROFILE) {
            return formatUpdatedItemsBehaviour(definition.reportUpdatedItemsBehaviour);
        }

        return <Link to={`/definitions/item-update-profiles/${definition.itemUpdateProfile?.id}`}>{definition.itemUpdateProfile?.name}</Link>;
    }

    function renderAction(_value: unknown, definition: VulReportDefinition) {
        return (
            <Space>
                <Button icon={<CopyOutlined/>} className={"ant-btn-icon-only"}
                        title={"Clone"}
                        onClick={() => navigate(`/definitions/vul-reports/clone/${definition.id}`)}/>

                <Button icon={<DeleteFilled/>} className={"ant-btn-icon-only"}
                        title={"Delete"}
                        onClick={() => {
                            Modal.confirm({
                                content: <>Do you really want to delete report definition <b>{definition.name}</b>? This will also delete all reports created from this definition.</>,
                                okText: "Delete",
                                cancelText: "Cancel",
                                onOk: () => onDeleteConfirm(definition)
                            });
                        }}/>
            </Space>
        )
    }

    function onDeleteConfirm(definition: VulReportDefinition) {
        vulReportDefinitionService.delete(definition)
            .then(() => {
                message.success(<>Vulnerability report definition <b>{definition.name}</b> deleted.</>);

                reload();
            });
    }

    function onFinishFilter(values: any) {
        const rqbQuery = createRqbQuery(values);

        filterFormPersister.saveState(values);

        tableHandler.onRqbSearchSubmit(rqbQuery);
    }

    function createRqbQuery(values: any) {
        const rqbQuery = {
            combinator: "and",
            rules: []
        }

        if (appContext.user.isSystemAdmin() && values.organizationIds?.length > 0) {
            rqbQuery.rules.push({
                field: "organization.id",
                operator: "in",
                value: values.organizationIds
            });
        }

        if (values.enabled !== undefined) {
            rqbQuery.rules.push({
                field: "enabled",
                operator: "eq",
                value: values.enabled
            });
        }

        return rqbQuery;
    }

    function reload() {
        return vulReportDefinitionService.getList(tableHandler.queryOptions).then(value => {
            tableHandler.updateTotal(value.total);

            setDefinitions(value);
        });
    }

    function onRow(definition: VulReportDefinition) {
        return {onDoubleClick: () => navigate(`/definitions/vul-reports/${definition.id}`)}
    }

}

export default VulReportDefinitionList;
